import React, { useEffect, useRef, useState } from "react";
import Isotope from "isotope-layout";
import ProjectDetailsModal from "./ProjectDetailsModal";
import T from "./Translate/T";

const Portfolio = () => {
  const isotope = useRef();
  const [filterKey, setFilterKey] = useState("*");
  const [imagesLoaded, setimagesLoaded] = useState(0);
  const [selectedProjectDetails, setSelectedProjectDetails] = useState();
  const [isOpen, setIsOpen] = useState(false);

  const htmlElement = document.getElementsByTagName("html")[0];
  const isRtl = htmlElement.getAttribute("dir") === "rtl";

  const filters = {
    CASESTUDY: "Case Studies",
  };

  const types = {
    IMAGE: "image",
    VIDEO: "video",
    DOCUMENT: "document",
    CASESTUDY: "casestudy",
  };

  const projectsData = [
    // ═══════════════════════════════════════════════════════════════════════
    // CASE STUDY 1: MUISCA REAL ESTATE PLATFORM
    // ═══════════════════════════════════════════════════════════════════════
    {
      title: "Muisca Real Estate Platform",
      subtitle: "Full-Stack Development · Next.js 15 · Laravel 12 · MySQL",
      type: types.CASESTUDY,
      thumbImage: "portfolio/muisca_landing_page.png",
      categories: [filters.CASESTUDY],
      caseStudy: {
        client: "Personal Project — Live Production",
        role: "Full Stack Engineer & System Architect",
        technologies: "Next.js 15 · TypeScript · Laravel 12 · PHP 8.3 · MySQL 8 · Nginx · PM2",
        date: "March 2026",
        liveUrl: "https://muisca.ca",
        brief:
          "A fully customized real estate platform featuring multi-language support for international users, interactive Google Maps integration, advanced property search, and role-based authentication. The platform supports up to 500+ property listings with high-resolution image galleries.",
        goal:
          "Deliver a scalable, secure, and user-friendly real estate solution that demonstrates full-stack expertise — from database architecture and RESTful API design to responsive front-end development and cloud deployment.",
        // Technical Overview Card Data
        technicalOverview: {
          liveUrls: [
            { service: "Website", url: "https://muisca.ca" },
            { service: "API", url: "https://api.muisca.ca" },
          ],
          architecture: [
            { layer: "Frontend", technology: "Next.js 15 (TypeScript)" },
            { layer: "Backend", technology: "Laravel 12 (PHP 8.3)" },
            { layer: "Database", technology: "MySQL 8" },
            { layer: "Web Server", technology: "Nginx" },
            { layer: "Process Mgr", technology: "PM2" },
          ],
          infrastructure: [
            { component: "Cloud Provider", details: "Hetzner (Helsinki, FI)" },
            { component: "Server", details: "2 vCPU AMD / 32 GB RAM" },
            { component: "Storage", details: "80 GB SSD" },
            { component: "SSL", details: "Let's Encrypt (Auto-renew)" },
            { component: "OS", details: "Ubuntu 22.04 LTS" },
          ],
          security: [
            { feature: "HTTPS / SSL", status: true },
            { feature: "CSRF Protection", status: true },
            { feature: "Sanctum Auth", status: true },
            { feature: "Role-Based Access", status: true },
            { feature: "API Rate Limiting", status: true },
          ],
          keyFeatures: [
            { name: "Multi-language Support", desc: "Google Translate API integration for international users" },
            { name: "Google Maps Integration", desc: "Interactive property maps with location markers" },
            { name: "Property Listings", desc: "Full CRUD with image gallery (up to 15 images/property)" },
            { name: "Advanced Search", desc: "Filter by type, location, price, bedrooms, amenities" },
            { name: "User Authentication", desc: "Laravel Breeze + Sanctum token-based auth" },
            { name: "Role-Based Access", desc: "Admin, Super-Admin, Agent roles via Spatie" },
            { name: "Responsive Design", desc: "Mobile-first Bootstrap 5 layout" },
            { name: "REST API", desc: "Clean API architecture for future mobile app integration" },
          ],
          capacity: [
            { metric: "Property Listings", value: "Up to 500+ listings" },
            { metric: "Images per Property", value: "Up to 15 high-res images" },
            { metric: "Storage Available", value: "80 GB SSD" },
            { metric: "Expected Uptime", value: "99.9%" },
          ],
        },
        // Slideshow Groups
        slideshowGroups: [
          {
            title: "Database Architecture",
            description: "Entity-Relationship Diagram showing the complete database schema",
            images: ["portfolio/erd_muisca.png"],
          },
          {
            title: "Application Screenshots",
            description: "Landing page and dashboard views",
            images: [
              "portfolio/muisca_landing_page.png",
              "portfolio/muisca_dashboard.png",
            ],
          },
        ],
        valueProposition: [
          {
            title: "Production-Ready Architecture",
            body: "Built with enterprise-grade technologies and deployed on reliable cloud infrastructure with automated SSL renewal and process management.",
          },
          {
            title: "Scalable & Secure",
            body: "Role-based access control, API rate limiting, and CSRF protection ensure the platform is secure while supporting growth.",
          },
          {
            title: "International Ready",
            body: "Multi-language support enables reaching a global audience, essential for real estate markets with international buyers.",
          },
        ],
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // CASE STUDY 2: ENTERPRISE WORKFLOW MANAGEMENT SYSTEM
    // ═══════════════════════════════════════════════════════════════════════
    {
      title: "Enterprise Workflow Management System",
      subtitle: "System Design & Business Analysis · UX Architecture",
      type: types.CASESTUDY,
      thumbImage: "portfolio/workflow.png",
      categories: [filters.CASESTUDY],
      caseStudy: {
        client: "Higher Education Institution (NDA)",
        role: "Full Stack Engineer & UX Designer",
        technologies: "PHP · Laravel · MySQL · JavaScript · React · LAMP Stack",
        date: "December 2023",
        brief:
          "A comprehensive system design for a faculty-facing web application enabling staff to apply for international experience funding as part of their curriculum. Submissions are capped per academic session and route through a hierarchical approval workflow spanning multiple stakeholder roles — from faculty applicant through departmental and institutional sign-off.",
        goal:
          "Demonstrate a methodological approach to transforming complex business requirements into technical specifications — covering database architecture, process decomposition, UX wireframes, and acceptance criteria — for both front-end and backend layers.",
        phases: [
          {
            number: "01",
            title: "Process Decomposition",
            description:
              "Before writing a single line of code, the business process was broken into distinct, named stages. Each stage maps to a responsible actor, a status, and a set of transitions. This stage diagram became the backbone of both the database schema and the UI workflow.",
            icon: "fas fa-sitemap",
          },
          {
            number: "02",
            title: "Database Architecture — 19-Entity ERD",
            description:
              "A relational model was designed around 19 entities including Application, Users, Stages, Budget, Itinerary, Course, Program, and more. Key design decisions: a generic inbox_id field routes ownership between roles at each stage; stage_id drives the workflow state machine; a session field scopes submissions per cycle.",
            icon: "fas fa-database",
          },
          {
            number: "03",
            title: "Mobile-First Wireframes",
            description:
              "Wireframes began on mobile as a deliberate constraint — if the flow works on a small screen, it works everywhere. Login, sign-up, and authentication screens were sketched first to establish the entry point. Chevron-style navigation was chosen for multi-step application forms.",
            icon: "fas fa-mobile-alt",
          },
          {
            number: "04",
            title: "Dashboard Mock-ups",
            description:
              "Dashboard screens were designed covering: user profile editing, user list management, course editing, course student assignment, and program creation. Each screen was mapped to a specific role permission level, ensuring the UI reflected the underlying authorization model.",
            icon: "fas fa-th-large",
          },
          {
            number: "05",
            title: "Workflow & Acceptance Criteria",
            description:
              "The approval workflow was modeled as a one-page scrolling layout showing all stages simultaneously, giving administrators a bird's-eye view of every application in the pipeline. Acceptance criteria were documented per stage including email notification mock-ups and KPI dashboards.",
            icon: "fas fa-tasks",
          },
        ],
        architecture: {
          stack: "PHP LAMP (Linux, Apache, MySQL, PHP) · Laravel Framework",
          entities: "19 Data Entities",
          keyEntities: [
            { name: "Application", note: "Central entity — holds status, stage, inbox owner, course, session" },
            { name: "Users", note: "Multi-role with institution and program foreign keys" },
            { name: "Stages", note: "Drives the workflow state machine from draft to final approval" },
            { name: "Budget", note: "Linked to application and expense types with a defined funding cap" },
            { name: "Itinerary", note: "Stores destination and travel schedule per application" },
          ],
          auth: "Role-based authorization with login / sign-up flows (OAuth 2.1 pattern)",
        },
        // Slideshow Groups - organized by section
        slideshowGroups: [
          {
            title: "Workflow Overview",
            description: "High-level process flow diagram showing the complete approval workflow",
            images: ["portfolio/workflow.png"],
          },
          {
            title: "Authentication & Authorization",
            description: "Login and sign-up wireframes with role-based access control",
            images: ["portfolio/authentication_authorization.png"],
          },
          {
            title: "Dashboard Administration",
            description: "Administrative interfaces for user management, courses, and programs",
            images: [
              "portfolio/dashboard_individual_summary_01.png",
              "portfolio/dashboard_individual_summary_02.png",
              "portfolio/dashboard_individual_summary_03.png",
              "portfolio/dashboard_individual_summary_04.png",
              "portfolio/dashboard_list_users.png",
              "portfolio/dashboard_program_add.png",
              "portfolio/dashboard_program_edition.png",
              "portfolio/dashboard_students_course_edition.png",
              "portfolio/dashboard_user_add.png",
              "portfolio/dashboard_course_edition.png",
            ],
          },
          {
            title: "Application Screens",
            description: "Multi-step application form with itinerary, budget, and workflow tracking",
            images: [
              "portfolio/application_applicants.png",
              "portfolio/application_itinerary.png",
              "portfolio/application_budget.png",
              "portfolio/application_summary_01.png",
              "portfolio/application_summary_02.png",
              "portfolio/application_details.png",
              "portfolio/application_workflow.png",
            ],
          },
          {
            title: "Database Architecture",
            description: "19-Entity ERD showing the complete relational data model",
            images: ["portfolio/erd_university.png"],
          },
        ],
        deliverables: [
          "Stage decomposition diagram",
          "19-entity ERD with full attribute definitions",
          "Mobile-first login & sign-up wireframes",
          "Dashboard screens (user, course, program management)",
          "Multi-step application input screens",
          "Workflow review — one-page scrolling layout",
          "Email notification mock-up",
          "KPI dashboard with printable metrics",
          "Acceptance criteria documentation per stage",
        ],
        valueProposition: [
          {
            title: "Robust Database Architecture",
            body: "A well-designed data model ensures accurate information representation and provides the foundation for reliable analytics, reporting, and decision-making.",
          },
          {
            title: "Clear Process Abstraction",
            body: "Breaking the process into named stages provides a structured framework for users and eliminates ambiguity between developer intent and business expectation.",
          },
          {
            title: "Intuitive, Responsive UX",
            body: "Mobile-first wireframes with Chevron navigation simplify complex multi-step workflows, improving user engagement across all device sizes.",
          },
        ],
      },
    },
  ];

  useEffect(() => {
    isotope.current = new Isotope(".portfolio-filter", {
      itemSelector: ".filter-item",
      layoutMode: "masonry",
      originLeft: !isRtl,
    });
    return () => isotope.current.destroy();
  }, []);

  useEffect(() => {
    if (imagesLoaded) {
      filterKey === "*"
        ? isotope.current.arrange({ filter: `*` })
        : isotope.current.arrange({ filter: `.${filterKey}` });
    }
  }, [filterKey, imagesLoaded]);

  const handleFilterKeyChange = (key) => () => setFilterKey(key);

  const getKeyByValue = (value) =>
    Object.keys(filters).find((key) => filters[key] === value);

  const getFilterClasses = (categories) => {
    if (categories.length > 0) {
      return categories.map((cat) => getKeyByValue(cat)).join(" ");
    }
  };

  return (
    <>
      <section id="portfolio" className="section bg-light">
        <div className="container">
          <p className="text-center mb-2 wow fadeInUp">
            <span className="bg-primary text-dark px-2"><T>Portfolio</T></span>
          </p>
          <h2 className="text-10 fw-600 text-center mb-5 wow fadeInUp">
            <T>Some of my most recent projects</T>
          </h2>

          {/* Filter Menu */}
          <ul className="portfolio-menu nav nav-tabs fw-600 justify-content-start justify-content-md-center border-bottom-0 mb-5 wow fadeInUp">
            <li className="nav-item">
              <button
                className={"nav-link " + (filterKey === "*" ? "active" : "")}
                onClick={handleFilterKeyChange("*")}
              >
                <T>All</T>
              </button>
            </li>
            {Object.keys(filters).map((oneKey, i) => (
              <li className="nav-item" key={i}>
                <button
                  className={
                    "nav-link " + (filterKey === oneKey ? "active" : "")
                  }
                  onClick={handleFilterKeyChange(oneKey)}
                >
                  <T>{filters[oneKey]}</T>
                </button>
              </li>
            ))}
          </ul>

          <div className="portfolio wow fadeInUp">
            <div className="row portfolio-filter filter-container g-4">
              {projectsData.map((project, index) => (
                <div
                  className={
                    "col-sm-6 col-lg-6 filter-item " +
                    getFilterClasses(project.categories)
                  }
                  key={index}
                  style={{ position: "relative" }}
                >
                  {/* Blur Background Effect */}
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      left: "10px",
                      right: "-10px",
                      bottom: "-10px",
                      backgroundImage: `url(${project.thumbImage})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      filter: "blur(20px)",
                      opacity: 0.4,
                      borderRadius: "12px",
                      transform: "scale(0.95)",
                      zIndex: 0,
                    }}
                  />
                  <div 
                    className="portfolio-box rounded-3"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: "rgba(255, 255, 255, 0.95)",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      overflow: "hidden",
                      height: "350px",
                    }}
                  >
                    {project.type === types.CASESTUDY && (
                      <div
                        style={{
                          position: "absolute",
                          top: "12px",
                          left: "12px",
                          zIndex: 2,
                          background: "#000",
                          color: "#fff",
                          fontSize: "10px",
                          fontWeight: "700",
                          letterSpacing: "1.5px",
                          textTransform: "uppercase",
                          padding: "4px 10px",
                          pointerEvents: "none",
                        }}
                      >
                        <T>Case Study</T>
                      </div>
                    )}
                    <div className="portfolio-img" style={{ height: "100%" }}>
                      <img
                        onLoad={() => setimagesLoaded(imagesLoaded + 1)}
                        className="img-fluid d-block portfolio-image"
                        src={project.thumbImage}
                        alt={project.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "center center",
                        }}
                      />
                      <div
                        className="portfolio-overlay"
                        onClick={() => {
                          setSelectedProjectDetails(projectsData[index]);
                          setIsOpen(true);
                        }}
                      >
                        <button className="popup-ajax stretched-link border-0 p-0">
                          {" "}
                        </button>
                        <div className="portfolio-overlay-details">
                          <p className="text-primary text-8">
                            {project.type === types.CASESTUDY && (
                              <i className="fas fa-layer-group"></i>
                            )}
                            {project.type === types.DOCUMENT && (
                              <i className="fas fa-file-alt"></i>
                            )}
                            {project.type === types.IMAGE && (
                              <i className="fas fa-image"></i>
                            )}
                            {project.type === types.VIDEO && (
                              <i className="fas fa-video"></i>
                            )}
                          </p>
                          <h5 className="text-white text-5">{project?.title}</h5>
                          {project?.subtitle && (
                            <span className="text-light" style={{ fontSize: "11px" }}>
                              {project.subtitle}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {isOpen && (
        <ProjectDetailsModal
          projectDetails={selectedProjectDetails}
          setIsOpen={setIsOpen}
        />
      )}
    </>
  );
};

export default Portfolio;